import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/**
 * Component id for the order fields the INVOICING desk owns, as a SECOND gate beside
 * `order-lc-fields`.
 *
 * Ported from V1's ACL rather than invented. `cmsacl.accesscontrol` holds two rows for
 * `invoicingclerk` — recno 21 (sysid `cms`, the order screen) and recno 62 (sysid
 * `updateall`, Change All) — and `setACL` loads BOTH for a cms session, keyed by sysid.
 * Their `acl_item.writefields` on `ordersall` are:
 *
 *   21 / cms         masterpro
 *   62 / updateall   billtoname, billto, billtorefno, consigneename, consigneecode,
 *                    masterpro, templatetype, ratecomments, sonumber
 *
 * `getACL` defaults every field to READ-ONLY, so those lists are the allow-list, not an
 * exception list — which is why the group had no editable order field at all here.
 *
 * DIVERGENCE, stated plainly: V1 could give Change All a wider field set than the order
 * screen because they are two screens reading two sysids. In 360 Change All IS the /pro
 * form — the same components, the same gates — so there is no seam to hang the
 * distinction on, and one set has to serve both. This is the updateall set, so these
 * three are editable on a single order too, where V1 allowed only masterpro. The
 * alternative (honour the cms set) would mean shipping a Change All that cannot change
 * what V1's Change All changed, which is the worse of the two.
 */
export const ORDER_INVOICING_FIELDS_COMPONENT_ID = 'order-invoicing-fields';

/**
 * The fields this component opens, as the keys `AddlInfoView.canEditLc` is called with
 * (NOT the legacy column names, and not always the bulk field name — Consignee's gate
 * key is `consigneeName` while its Change All checkbox is `consigneeCode`).
 *
 * V1's updateall set has nine columns; these are the three 360 actually renders as
 * editable order fields. The other six are not withheld, they have nowhere to go:
 *
 *   billto / billtoname   the ORDER's Bill To has no editable order-form field; the one
 *                         /pro edits is the INVOICE's, on the Invoicing tab, which is
 *                         `pro-invoicing-write`'s surface (and V1 grants that too, via
 *                         62's `invoice.billto`).
 *   billtorefno           an `invoice` column, same surface as above.
 *   ratecomments          HAS a working Change All checkbox — on the Invoicing tab, via
 *                         InvBulkCheckbox rather than a `bulkFieldName`, and it is an
 *                         ORDER-arm field so `field_definitions.is_change_all` governs
 *                         it (flagged 1 in prod). It is absent here because it carries
 *                         no component permission AT ALL: its gate is
 *                         `fieldEditable('rateComments', editable)`, and `editable` only
 *                         means "a persisted pro". There is nothing for this component
 *                         to open — the field is already wider than v1, which gave it to
 *                         csr and invoicingclerk/updateall only. Worth its own gate one
 *                         day; not this change's business.
 *   sonumber              has no `view_key` in field_definitions and no /pro render site
 *                         at all, so nothing can edit it here, LC or otherwise.
 *
 * Adding a field is this set plus nothing else: the gate is read through canEditLc, so
 * the checkbox, the input and the read-only fallback all follow from one entry.
 */
export const ORDER_INVOICING_FIELD_KEYS: ReadonlySet<string> = new Set([
  'masterPro',      // ordersall.masterpro   — which orders bill together
  'consigneeName',  // ordersall.consigneecode/consigneename (gate key is the name)
  'templateType',   // ordersall.templatetype — 360 labels it Rate Zone
]);

/**
 * Whether the current user holds the invoicing field gate.
 *
 * Ships **defaultDisabled**, like every gate it sits beside: until an admin grants a
 * group edit access on `order-invoicing-fields`, this answers false and the three fields
 * behave exactly as they did — editable for `order-lc-fields` holders, read-only for
 * everyone else. Migration 483 seeds the grant for GWI360-InvoiceClerk.
 *
 * ORed with the LC gate at the call site, never ANDed, so it can only ADD to a field's
 * audience. That is also what V1 does: `setACL` merges every role the user holds at a
 * given priority into one GROUP_CONCAT and applies `writefields` LAST, so a field
 * writable by EITHER role is writable for someone in both. A user in `csr` and
 * `invoicingclerk` gets the union, not the intersection, and neither role can take a
 * field away from the other — only a HIGHER-priority role can (see `orderinvoiced`,
 * priority 900, which is what `order_field_display_rules` ports).
 */
export const useCanEditInvoicingFields = (): boolean =>
  useResolvedAccessLevel(ORDER_INVOICING_FIELDS_COMPONENT_ID, false, true) >= 2;
